// src/slices/printHouse/reducer.ts
// README.md mục 7 — copy pattern slices/ticketType/reducer.ts. Khác biệt: PrintHouse dùng
// DELETE thật (deletePrintHouse) thay vì lock/unlock — BE có endpoint xóa mềm riêng, không có
// khái niệm "khóa" như TicketType.

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  PrintHouse,
  PrintHouseFilters,
  PrintHouseModalState,
  PrintHousePagination,
} from 'pages/PrintHouseManagement/types';
import { fetchPrintHouses, createPrintHouse, updatePrintHouse, deletePrintHouse } from './thunk';

interface PrintHouseState {
  printHouses: PrintHouse[];
  selectedPrintHouse: PrintHouse | null;
  pagination: PrintHousePagination;
  filters: PrintHouseFilters;
  loading: boolean;
  submitting: boolean;
  error: string | null;
  modalState: PrintHouseModalState;
}

const initialState: PrintHouseState = {
  printHouses: [],
  selectedPrintHouse: null,
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalItems: 0,
    totalPages: 0,
  },
  filters: {
    searchTerm: '',
    isActive: 'All',
  },
  loading: false,
  submitting: false,
  error: null,
  modalState: {
    create: false,
    edit: false,
  },
};

const printHouseSlice = createSlice({
  name: 'PrintHouse',
  initialState,
  reducers: {
    setSelectedPrintHouse: (state, action: PayloadAction<PrintHouse | null>) => {
      state.selectedPrintHouse = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
      state.pagination.currentPage = 1;
    },
    setFilters: (state, action: PayloadAction<Partial<PrintHouseFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = { searchTerm: '', isActive: 'All' };
      state.pagination.currentPage = 1;
    },
    openModal: (state, action: PayloadAction<keyof PrintHouseModalState>) => {
      state.modalState[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof PrintHouseModalState>) => {
      state.modalState[action.payload] = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch danh sách (phân trang)
      .addCase(fetchPrintHouses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPrintHouses.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.printHouses = payload.items || payload.data || payload || [];
        state.pagination.totalItems = payload.totalItems ?? payload.total ?? state.printHouses.length;
        state.pagination.totalPages = payload.totalPages ?? Math.ceil(state.pagination.totalItems / state.pagination.pageSize);
      })
      .addCase(fetchPrintHouses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Tạo mới
      .addCase(createPrintHouse.pending, (state) => {
        state.submitting = true;
      })
      .addCase(createPrintHouse.fulfilled, (state) => {
        state.submitting = false;
        state.modalState.create = false;
      })
      .addCase(createPrintHouse.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Cập nhật
      .addCase(updatePrintHouse.pending, (state) => {
        state.submitting = true;
      })
      .addCase(updatePrintHouse.fulfilled, (state) => {
        state.submitting = false;
        state.modalState.edit = false;
        state.selectedPrintHouse = null;
      })
      .addCase(updatePrintHouse.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Xóa
      .addCase(deletePrintHouse.pending, (state) => {
        state.submitting = true;
      })
      .addCase(deletePrintHouse.fulfilled, (state, action) => {
        state.submitting = false;
        state.printHouses = state.printHouses.filter((p) => p.id !== action.payload);
      })
      .addCase(deletePrintHouse.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedPrintHouse,
  setError,
  clearError,
  setPage,
  setPageSize,
  setFilters,
  clearFilters,
  openModal,
  closeModal,
} = printHouseSlice.actions;

export default printHouseSlice.reducer;
